import React, { useEffect, useState } from 'react';
import { fetchPageBySlug, getLocalizedValue } from '../lib/cms';
import usePageMetadata from '../hooks/usePageMetadata';
import ContactForm from '../components/ContactForm';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

export default function Contact() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    async function loadData() {
      try {
        const page = await fetchPageBySlug('kontakt');
        setPageData(page);
      } catch (err) {
        console.error('Error loading Contact page:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Set page metadata
  const title = pageData ? getLocalizedValue(pageData.meta_title, language) : 'Kontakt René Vilsgaard - Insightly ApS';
  const description = pageData ? getLocalizedValue(pageData.meta_description, language) : 'Kontakt Insightly ApS for en uforpligtende snak om dit ERP-projekt. Ring, send en mail eller ræk ud på LinkedIn.';
  usePageMetadata(title, description);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-offwhite">
        <div className="flex flex-col items-center gap-3">
          <i className="fa-solid fa-spinner animate-spin text-3xl text-petrol"></i>
          <p className="text-sm text-navy-900/60 font-medium">Henter indhold...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-offwhite py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Editorial Header */}
        <div className="max-w-3xl mb-16 space-y-4">
          <span className="text-xs uppercase tracking-[0.25em] text-petrol font-bold">// {t.contact.contactTag}</span>
          <h1 className="text-4xl sm:text-5xl font-serif font-medium text-navy-900 leading-tight">
            {pageData ? getLocalizedValue(pageData.title, language) : t.contact.title}
          </h1>
          <p className="text-lg text-navy-900/60 font-sans">
            {t.contact.subtitle}
          </p>
        </div>

        {/* Contact Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column - Contact Details */}
          <div className="lg:col-span-5 space-y-8">
            {/* Intro Text from CMS */}
            <div className="bg-white rounded-none border border-bluegray p-6 sm:p-8 shadow-xl">
              <div 
                className="prose prose-navy text-sm sm:text-base text-charcoal/80 leading-relaxed font-sans"
                dangerouslySetInnerHTML={{ __html: pageData ? getLocalizedValue(pageData.content, language) : '' }}
              />
            </div>

            {/* Direct Details Card */}
            <div className="bg-white rounded-none border border-bluegray p-6 sm:p-8 shadow-xl space-y-6">
              <h3 className="font-serif text-lg font-bold text-navy-900">{t.contact.infoTitle}</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-none bg-navy-50 text-navy-900 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-phone text-petrol"></i>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-navy-900/50">{t.footer.phone}</p>
                    <a href="tel:30655509" className="text-base font-bold text-navy-900 hover:text-petrol transition-colors">30 65 55 09</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-none bg-navy-50 text-navy-900 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-envelope text-petrol"></i>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-navy-900/50">{t.footer.email}</p>
                    <a href="mailto:rvi@insightly.dk" className="text-base font-bold text-navy-900 hover:text-petrol transition-colors break-all">rvi@insightly.dk</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-none bg-navy-50 text-navy-900 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-location-dot text-petrol"></i>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-navy-900/50">{t.footer.address}</p>
                    <p className="text-base font-bold text-navy-900">Dronningemaen 68, 5700 Svendborg</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 pt-4 border-t border-bluegray/60">
                  <div className="h-10 w-10 rounded-none bg-navy-50 text-navy-900 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-file-certificate text-petrol"></i>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-navy-900/50">{t.contact.companyInfo}</p>
                    <p className="text-base font-bold text-navy-900">Insightly ApS · {t.footer.cvr} 43779362</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="lg:col-span-7">
            <ContactForm />
          </div>
        </div>
      </div>
    </div>
  );
}
